import { WebshopFavoritesProduct } from '@cldsystems/ws-api-client';
import { StateCreator } from 'zustand';
import { FavoritesActionButtonsEnum } from '@/types/action-buttons/action-buttons';
import { LocalFavorites } from '@/types/favorites/local-favorites';

export type FavoritePendingAction = 'add' | 'remove';

export type FavoritePendingOperation = {
  status: 'pending';
  requestedAction: FavoritePendingAction;
  buttonType: FavoritesActionButtonsEnum | undefined;
  operationId: string;
};

export type FavoritesSlice = {
  favorites: LocalFavorites;
  setFavourites: (products: LocalFavorites) => void;
  addProductToFavorites: (product: WebshopFavoritesProduct) => void;
  removeProductFromFavorites: (product: WebshopFavoritesProduct) => void;
  resetFavorites: () => void;
  favoritePendingByProductId: Record<string, FavoritePendingOperation>;
  markFavoritePending: (
    productId: string,
    requestedAction: FavoritePendingAction,
    buttonType: FavoritesActionButtonsEnum | undefined,
    operationId: string,
  ) => void;
  clearFavoritePending: (productId: string, operationId?: string) => void;
  getFavoritePending: (productId: string) => FavoritePendingOperation | undefined;
  isFavoritePending: (productId: string, buttonType?: FavoritesActionButtonsEnum) => boolean;
  resetFavoritePending: () => void;
};

const defaultValue: LocalFavorites = {
  products: [],
};

export const createFavoritesSlice: StateCreator<FavoritesSlice> = (set, get) => ({
  favorites: defaultValue,
  favoritePendingByProductId: {},
  markFavoritePending: (productId, requestedAction, buttonType, operationId) =>
    set(state => ({
      favoritePendingByProductId: {
        ...state.favoritePendingByProductId,
        [productId]: {
          status: 'pending',
          requestedAction,
          buttonType,
          operationId,
        },
      },
    })),
  clearFavoritePending: (productId, operationId) =>
    set(state => {
      const pendingOperation = state.favoritePendingByProductId[productId];

      if (!pendingOperation || (operationId && pendingOperation.operationId !== operationId)) {
        return {};
      }

      const favoritePendingByProductId = { ...state.favoritePendingByProductId };
      delete favoritePendingByProductId[productId];

      return { favoritePendingByProductId };
    }),
  getFavoritePending: productId => get().favoritePendingByProductId[productId],
  isFavoritePending: (productId, buttonType) => {
    const pendingOperation = get().favoritePendingByProductId[productId];

    if (!pendingOperation) {
      return false;
    }

    if (!buttonType) {
      return true;
    }

    return pendingOperation.buttonType === buttonType;
  },
  resetFavoritePending: () => set(() => ({ favoritePendingByProductId: {} })),
  setFavourites: value =>
    set(() => ({
      favorites: {
        ...value,
      },
    })),
  resetFavorites: () => set(() => ({ favorites: defaultValue, favoritePendingByProductId: {} })),
  addProductToFavorites: product =>
    set(state => {
      const isProductInFavorites = state.favorites.products.find(p => p.id === product.id);

      let products = [...state.favorites.products];

      const addNewProduct = () => {
        products = [...state.favorites.products, product];
      };

      if (!isProductInFavorites) {
        addNewProduct();
      }

      return {
        favorites: {
          products: products,
        },
      };
    }),
  removeProductFromFavorites: product =>
    set(state => ({
      favorites: {
        products: state.favorites.products.filter(p => p.id !== product.id),
      },
    })),
});
